import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SpeedIcon from '@mui/icons-material/Speed';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { format } from 'date-fns';
import { formatNumber } from '@/utils/locale';

interface MileageRequiredDialogProps {
  open: boolean;
  onConfirm: (mileage: number) => void;
  onCancel: () => void;
  loading?: boolean;
  latestMileage?: {
    mileage: number;
    recordedAt: string;
  } | null;
}

export function MileageRequiredDialog({
  open,
  onConfirm,
  onCancel,
  loading,
  latestMileage,
}: MileageRequiredDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [mileageInput, setMileageInput] = useState('');

  const enteredMileage = mileageInput ? Number(mileageInput) : null;
  const isMileageLow =
    enteredMileage != null &&
    enteredMileage > 0 &&
    latestMileage?.mileage != null &&
    enteredMileage < latestMileage.mileage;

  const handleConfirm = () => {
    if (enteredMileage != null && enteredMileage >= 0) {
      onConfirm(enteredMileage);
    }
  };

  const handleClose = () => {
    setMileageInput('');
    onCancel();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('serviceActions.mileageRequired')}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('serviceActions.mileageRequired')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SpeedIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {t('serviceActions.mileageRequiredDesc')}
          </Typography>
        </Box>

        <TextField
          label={t('serviceActions.currentMileage')}
          placeholder={t('create.mileagePlaceholder')}
          value={mileageInput}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            setMileageInput(val);
          }}
          type="text"
          inputMode="numeric"
          size="small"
          fullWidth
          autoFocus
          color={isMileageLow ? 'warning' : undefined}
          helperText={
            latestMileage?.mileage != null && latestMileage.recordedAt
              ? t('create.mileageLatest', {
                  mileage: formatNumber(latestMileage.mileage),
                  date: format(new Date(latestMileage.recordedAt), 'd.M.yyyy'),
                })
              : t('create.mileageNoHistory')
          }
        />

        {isMileageLow && (
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mt: 2 }}>
            {t('create.mileageLowerThanLatest', {
              latest: formatNumber(latestMileage!.mileage!),
            })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !enteredMileage || enteredMileage < 0}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {tc('confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
